import * as THREE from 'three'
import {
  GaussianSplatMesh,
  type GaussianSplatMeshOptions,
} from '@/plugins/gsplat/core/GaussianSplatMesh'
import { SogsData, type SogsMeta } from '@/plugins/gsplat/core/SogsData'
import { SplatData } from '@/plugins/gsplat/core/SplatData'
import {
  beginGSplatResourceAttempt,
  measureGSplatLoadStageSync,
} from '@/plugins/gsplat/telemetry/GSplatLoadTelemetry'
import { applyAssetRootTransform } from '@/plugins/gsplat/utils/applyAssetRootTransform'
import { getLoggerManager } from '@/shared/logging'
import type { GSplatLoadTelemetryObserver } from '@/shared/types/gsplatTelemetry'

/** SOG 加载 logger（命名空间 gsplat:loader），perf 埋点经 log.perf 输出 */
const log = getLoggerManager().getLogger('gsplat:loader')

/**
 * SOG LOD 元数据结构
 */
export interface SOGLodMeta {
  /** 资产内禀世界偏移；未提供时默认为 [0, 0, 0] */
  offset?: [number, number, number]
  /** LOD 层级数量 */
  lodLevels: number
  /** 环境/场景附加元信息，当前未使用 */
  environment: unknown | null
  /** bundle 内各子块 WebP 文件相对路径列表 */
  filenames: string[]
  /** 八叉树根节点 */
  tree: SOGNode
}

/** 校验并返回 Gaussian LOD 资产的内禀偏移。 */
export function getSOGLodOffset(data: unknown): [number, number, number] {
  if (typeof data !== 'object' || data === null) {
    throw new Error('[SOGLoader] lod-meta.json 必须是对象')
  }

  if (!('offset' in data)) {
    return [0, 0, 0]
  }

  const offset = (data as { offset?: unknown }).offset
  if (
    !Array.isArray(offset) ||
    offset.length !== 3 ||
    !offset.every((value) => typeof value === 'number' && Number.isFinite(value))
  ) {
    throw new Error('[SOGLoader] lod-meta.json.offset 必须是三个有限数')
  }

  return [offset[0], offset[1], offset[2]]
}

/** 校验 Gaussian LOD 元数据的坐标契约。 */
export function assertValidSOGLodMeta(data: unknown): asserts data is SOGLodMeta {
  getSOGLodOffset(data)
}

/**
 * SOG 八叉树节点
 */
export interface SOGNode {
  /** 节点包围盒（本地坐标） */
  bound: {
    min: [number, number, number]
    max: [number, number, number]
  }
  /** 子节点数组，叶节点可省略 */
  children?: SOGNode[]
  /** LOD 分段信息，key 为 LOD 层级 */
  lods?: Record<string, { file: number; offset: number; count: number }>
}

/**
 * SOG 子块元数据
 */
export interface SOGChunkMeta {
  /** SOG 格式版本号 */
  version: number
  /** 资产生成器信息 */
  asset?: { generator: string }
  /** 当前子块 splat 数量 */
  count: number
  /** 位置量化参数及纹理文件 */
  means: {
    mins: [number, number, number]
    maxs: [number, number, number]
    files: string[]
  }
  /** 缩放量化参数及纹理文件 */
  scales: {
    mins?: [number, number, number]
    maxs?: [number, number, number]
    codebook: number[]
    files: string[]
  }
  /** 四元数纹理文件 */
  quats: {
    files: string[]
  }
  /** SH DC 量化参数及纹理文件 */
  sh0: {
    mins?: [number, number, number, number]
    maxs?: [number, number, number, number]
    codebook: number[]
    files: string[]
  }
  /** SH 高阶系数信息，无高阶 SH 时省略 */
  shN?: {
    count?: number
    bands?: number
    mins?: number
    maxs?: number
    codebook: number[]
    files: string[]
  }
}

/**
 * SOG v1 格式元数据（用于类型安全的升级）
 */
interface SOGChunkMetaV1 {
  means?: {
    shape?: [number]
    mins?: [number, number, number]
    maxs?: [number, number, number]
    files?: string[]
  }
  scales?: {
    mins?: [number, number, number]
    maxs?: [number, number, number]
    files?: string[]
  }
  quats?: {
    files?: string[]
  }
  sh0?: {
    mins?: [number, number, number, number]
    maxs?: [number, number, number, number]
    files?: string[]
  }
  shN?: {
    mins?: number
    maxs?: number
    files?: string[]
  }
}

/**
 * SOG 加载选项
 */
export interface SOGLoaderOptions {
  /** 初始加载的LOD级别 (默认: 0) */
  initialLod?: number
  /** 最大同时加载的块数 (默认: 8) */
  maxConcurrentLoads?: number
  /** 是否启用调试日志 */
  debug?: boolean
  /** 进度回调 */
  onProgress?: (loaded: number, total: number, stage: string) => void
  /** 是否保留SOGS压缩格式（默认 true，节省 5-7x 内存） */
  keepCompressed?: boolean
  /** 中止信号 */
  signal?: AbortSignal
  /** 是否携带跨域 Cookie（用于 CloudFront 签名 Cookie 等场景） */
  withCredentials?: boolean
  /** 单次模型加载的只读遥测 observer */
  telemetry?: GSplatLoadTelemetryObserver
}

/**
 * SOG (Splat Octree Graph) LOD 格式加载器
 *
 * 支持加载 SuperSplat/splat-transform 生成的 LOD 格式文件
 *
 * 格式特点：
 * - 基于八叉树的空间分割
 * - 使用 WebP 图像存储压缩数据
 * - 支持多级 LOD
 * - 使用码本量化压缩
 */
export class SOGLoader {
  private baseUrl: string = ''
  private lodMeta: SOGLodMeta | null = null
  private chunkMetas: Map<number, SOGChunkMeta> = new Map()
  private options: SOGLoaderOptions

  constructor(options: SOGLoaderOptions = {}) {
    this.options = {
      initialLod: 0,
      maxConcurrentLoads: 8,
      debug: false,
      ...options,
    }
  }

  private fetchInit(extra?: RequestInit): RequestInit {
    const init: RequestInit = {}
    if (this.options.withCredentials) {
      init.credentials = 'include'
    }
    if (this.options.signal) {
      init.signal = this.options.signal
    }
    return extra ? { ...init, ...extra } : init
  }

  /**
   * 更新加载器配置（运行时可调整并发等参数）。
   *
   * @param options - 局部配置，仅覆盖提供的字段
   */
  updateOptions(options: Partial<SOGLoaderOptions>): void {
    this.options = {
      ...this.options,
      ...options,
    }
  }

  /**
   * 加载 SOG LOD 文件
   * @param url lod-meta.json 的 URL
   * @returns 合并后的 SplatData 或 SogsData（取决于 keepCompressed 选项）
   */
  async load(url: string): Promise<SplatData | SogsData> {
    // 检查中止信号
    if (this.options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    // 解析基础URL（先解码再提取，避免编码路径导致 baseUrl 提取错误）
    const decodedUrl = decodeURIComponent(url)
    this.baseUrl = decodedUrl.substring(0, decodedUrl.lastIndexOf('/') + 1)

    // 1. 加载主元数据
    this.options.onProgress?.(0, 100, '加载元数据')
    const response = await fetch(url, this.fetchInit())
    if (!response.ok) {
      throw new Error(`加载失败: ${response.status} ${response.statusText}`)
    }
    const lodMeta: unknown = await response.json()
    assertValidSOGLodMeta(lodMeta)
    this.lodMeta = lodMeta

    // 检查中止信号
    if (this.options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    // 2. 收集需要加载的块
    const chunksToLoad = this.collectChunksForLod(this.options.initialLod || 0)

    // 3. 并行加载所有块
    const totalChunks = chunksToLoad.length
    let loadedCount = 0

    const chunkDataList: (SplatData | SogsData)[] = []

    try {
      // 分批并行加载
      const batchSize = this.options.maxConcurrentLoads || 8
      for (let i = 0; i < chunksToLoad.length; i += batchSize) {
        // 检查中止信号
        if (this.options.signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError')
        }

        const batch = chunksToLoad.slice(i, i + batchSize)
        const results = await Promise.all(
          batch.map(async (chunkInfo) => {
            const data = await this.loadChunk(
              chunkInfo.fileIndex,
              chunkInfo.offset,
              chunkInfo.count
            )
            loadedCount++
            this.options.onProgress?.(
              loadedCount,
              totalChunks,
              `加载块 ${loadedCount}/${totalChunks}`
            )
            return data
          })
        )
        chunkDataList.push(...results)
      }
    } catch (error) {
      // 清理已加载的资源
      for (const data of chunkDataList) {
        if (data instanceof SogsData) {
          data.dispose()
        }
      }
      throw error
    }

    // 4. 合并所有块数据
    this.options.onProgress?.(totalChunks, totalChunks, '合并数据')

    // 如果用户要求保留压缩格式（默认），返回 SogsData
    if (this.options.keepCompressed !== false) {
      const mergedSogs = this.mergeSogsData(chunkDataList)
      return mergedSogs
    } else {
      // 否则解压为 SplatData
      const mergedData = this.mergeSplatData(chunkDataList)
      return mergedData
    }
  }

  /**
   * 加载 SOG LOD 并构建 GaussianSplatMesh。
   *
   * @param url - lod-meta.json 的 URL
   * @param meshOptions - 可选 Mesh 构建参数
   * @returns 已绑定数据的 GaussianSplatMesh
   */
  async loadMesh(url: string, meshOptions?: GaussianSplatMeshOptions): Promise<GaussianSplatMesh> {
    const splatData = await this.load(url)
    const mesh = new GaussianSplatMesh(splatData, meshOptions)
    const offset = getSOGLodOffset(this.lodMeta)
    applyAssetRootTransform(mesh, offset)
    return mesh
  }

  /**
   * 收集指定 LOD 级别需要加载的块
   */
  private collectChunksForLod(
    lodLevel: number
  ): Array<{ fileIndex: number; offset: number; count: number }> {
    const chunks: Array<{ fileIndex: number; offset: number; count: number }> = []

    const collectFromNode = (node: SOGNode) => {
      // 检查该节点是否有指定LOD级别的数据
      if (node.lods) {
        const lodKey = lodLevel.toString()
        if (node.lods[lodKey]) {
          chunks.push({
            fileIndex: node.lods[lodKey].file,
            offset: node.lods[lodKey].offset,
            count: node.lods[lodKey].count,
          })
        }
      }

      // 递归处理子节点
      if (node.children) {
        for (const child of node.children) {
          collectFromNode(child)
        }
      }
    }

    if (this.lodMeta?.tree) {
      collectFromNode(this.lodMeta.tree)
    }

    return chunks
  }

  /**
   * 加载单个块
   */
  private async loadChunk(
    fileIndex: number,
    offset: number,
    count: number
  ): Promise<SplatData | SogsData> {
    // 检查中止信号
    if (this.options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    // 获取块元数据
    let chunkMeta = this.chunkMetas.get(fileIndex)
    if (!chunkMeta) {
      const metaUrl = this.baseUrl + this.lodMeta?.filenames[fileIndex]
      const response = await fetch(metaUrl, this.fetchInit())
      chunkMeta = await response.json()
      this.chunkMetas.set(fileIndex, chunkMeta!)
    }

    // 计算块目录
    const chunkDir = this.lodMeta?.filenames[fileIndex].replace('/meta.json', '/')

    if (!chunkDir) {
      throw new Error('[SOGLoader] 无法计算块目录')
    }

    // 并行加载所有WebP文件
    const [meansData, scalesData, quatsData, sh0Data, shNData] = await Promise.all([
      this.loadMeansWebP(chunkDir, chunkMeta!),
      this.loadScalesWebP(chunkDir, chunkMeta!),
      this.loadQuatsWebP(chunkDir, chunkMeta!),
      this.loadSH0WebP(chunkDir, chunkMeta!),
      this.loadShNWebP(chunkDir, chunkMeta!),
    ])

    // 根据选项返回压缩格式或解压格式
    if (this.options.keepCompressed !== false) {
      return this.extractChunkSogsData(
        chunkMeta!,
        meansData,
        scalesData,
        quatsData,
        sh0Data,
        shNData,
        offset,
        count
      )
    } else {
      return this.extractChunkData(
        chunkMeta!,
        meansData,
        scalesData,
        quatsData,
        sh0Data,
        offset,
        count
      )
    }
  }

  /**
   * 加载位置 WebP 数据
   */
  private async loadMeansWebP(
    chunkDir: string,
    meta: SOGChunkMeta
  ): Promise<{ low: Uint8Array; high: Uint8Array }> {
    const [lowResult, highResult] = await Promise.all([
      this.loadWebPAsImageData(this.baseUrl + chunkDir + meta.means.files[0]),
      this.loadWebPAsImageData(this.baseUrl + chunkDir + meta.means.files[1]),
    ])
    return { low: lowResult.data, high: highResult.data }
  }

  /**
   * 加载缩放 WebP 数据
   */
  private async loadScalesWebP(chunkDir: string, meta: SOGChunkMeta): Promise<Uint8Array> {
    const result = await this.loadWebPAsImageData(this.baseUrl + chunkDir + meta.scales.files[0])
    return result.data
  }

  /**
   * 加载四元数 WebP 数据
   */
  private async loadQuatsWebP(chunkDir: string, meta: SOGChunkMeta): Promise<Uint8Array> {
    const result = await this.loadWebPAsImageData(this.baseUrl + chunkDir + meta.quats.files[0])
    return result.data
  }

  /**
   * 加载 SH0 WebP 数据
   */
  private async loadSH0WebP(chunkDir: string, meta: SOGChunkMeta): Promise<Uint8Array> {
    const result = await this.loadWebPAsImageData(this.baseUrl + chunkDir + meta.sh0.files[0])
    return result.data
  }

  /**
   * 加载 SHN WebP 数据（球谐高阶系数）
   * 参考 PlayCanvas sogs.js: shN[0] = sh_centroids, shN[1] = sh_labels
   * 返回数据和 shCentroids 的尺寸信息
   */
  private async loadShNWebP(
    chunkDir: string,
    meta: SOGChunkMeta
  ): Promise<{
    shCentroids: Uint8Array
    shLabels: Uint8Array
    shCentroidsWidth: number
    shCentroidsHeight: number
  } | null> {
    if (!meta.shN?.files || meta.shN.files.length < 2) {
      return null
    }
    const [centroidsResult, labelsResult] = await Promise.all([
      this.loadWebPAsImageData(this.baseUrl + chunkDir + meta.shN.files[0]),
      this.loadWebPAsImageData(this.baseUrl + chunkDir + meta.shN.files[1]),
    ])
    return {
      shCentroids: centroidsResult.data,
      shLabels: labelsResult.data,
      shCentroidsWidth: centroidsResult.width,
      shCentroidsHeight: centroidsResult.height,
    }
  }

  /**
   * 加载 WebP 文件并返回像素数据和尺寸
   */
  private async loadWebPAsImageData(
    url: string
  ): Promise<{ data: Uint8Array; width: number; height: number }> {
    // 检查中止信号
    if (this.options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    return new Promise((resolve, reject) => {
      const img = new Image()
      img.crossOrigin = 'anonymous'

      // 监听中止信号
      const abortHandler = () => {
        img.src = ''
        reject(new DOMException('Aborted', 'AbortError'))
      }

      if (this.options.signal) {
        this.options.signal.addEventListener('abort', abortHandler, { once: true })
      }

      img.onload = () => {
        if (this.options.signal) {
          this.options.signal.removeEventListener('abort', abortHandler)
        }

        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('无法创建 canvas context'))
          return
        }

        ctx.drawImage(img, 0, 0)
        const imageData = ctx.getImageData(0, 0, img.width, img.height)
        resolve({
          data: new Uint8Array(imageData.data.buffer),
          width: img.width,
          height: img.height,
        })
      }

      img.onerror = () => {
        if (this.options.signal) {
          this.options.signal.removeEventListener('abort', abortHandler)
        }
        reject(new Error(`加载图片失败: ${url}`))
      }

      img.src = url
    })
  }

  /**
   * 从加载的数据中提取指定范围的 splat
   */
  private extractChunkData(
    meta: SOGChunkMeta,
    meansData: { low: Uint8Array; high: Uint8Array },
    scalesData: Uint8Array,
    quatsData: Uint8Array,
    sh0Data: Uint8Array,
    offset: number,
    count: number
  ): SplatData {
    // 分配存储
    const x = new Float32Array(count)
    const y = new Float32Array(count)
    const z = new Float32Array(count)
    const scale_0 = new Float32Array(count)
    const scale_1 = new Float32Array(count)
    const scale_2 = new Float32Array(count)
    const rot_0 = new Float32Array(count)
    const rot_1 = new Float32Array(count)
    const rot_2 = new Float32Array(count)
    const rot_3 = new Float32Array(count)
    const f_dc_0 = new Float32Array(count)
    const f_dc_1 = new Float32Array(count)
    const f_dc_2 = new Float32Array(count)
    const opacity = new Float32Array(count)

    // 位置范围
    const mins = meta.means.mins
    const maxs = meta.means.maxs
    const ranges = [maxs[0] - mins[0], maxs[1] - mins[1], maxs[2] - mins[2]]

    // 码本
    const scalesCodebook = meta.scales.codebook
    const sh0Codebook = meta.sh0.codebook

    for (let i = 0; i < count; i++) {
      const srcIdx = offset + i
      const pixelOffset = srcIdx * 4 // RGBA

      // 解码位置 (16位精度，分高低位存储)
      const lowR = meansData.low[pixelOffset]
      const lowG = meansData.low[pixelOffset + 1]
      const lowB = meansData.low[pixelOffset + 2]
      const highR = meansData.high[pixelOffset]
      const highG = meansData.high[pixelOffset + 1]
      const highB = meansData.high[pixelOffset + 2]

      // 合并高低位
      const px = ((highR << 8) | lowR) / 65535
      const py = ((highG << 8) | lowG) / 65535
      const pz = ((highB << 8) | lowB) / 65535

      // 反量化到实际坐标
      x[i] = mins[0] + px * ranges[0]
      y[i] = mins[1] + py * ranges[1]
      z[i] = mins[2] + pz * ranges[2]

      // 解码缩放 (使用码本索引)
      const scaleIdx0 = scalesData[pixelOffset]
      const scaleIdx1 = scalesData[pixelOffset + 1]
      const scaleIdx2 = scalesData[pixelOffset + 2]
      scale_0[i] = scalesCodebook[scaleIdx0] ?? -10
      scale_1[i] = scalesCodebook[scaleIdx1] ?? -10
      scale_2[i] = scalesCodebook[scaleIdx2] ?? -10

      // 解码四元数 (归一化的 uint8)
      const qx = (quatsData[pixelOffset] / 255) * 2 - 1
      const qy = (quatsData[pixelOffset + 1] / 255) * 2 - 1
      const qz = (quatsData[pixelOffset + 2] / 255) * 2 - 1
      const qw = (quatsData[pixelOffset + 3] / 255) * 2 - 1

      // 归一化四元数
      const qLen = Math.sqrt(qx * qx + qy * qy + qz * qz + qw * qw)
      rot_0[i] = qw / qLen // w
      rot_1[i] = qx / qLen // x
      rot_2[i] = qy / qLen // y
      rot_3[i] = qz / qLen // z

      // 解码 SH0 (DC 颜色，使用码本)
      const sh0Idx0 = sh0Data[pixelOffset]
      const sh0Idx1 = sh0Data[pixelOffset + 1]
      const sh0Idx2 = sh0Data[pixelOffset + 2]
      const sh0Alpha = sh0Data[pixelOffset + 3]

      f_dc_0[i] = sh0Codebook[sh0Idx0] ?? 0
      f_dc_1[i] = sh0Codebook[sh0Idx1] ?? 0
      f_dc_2[i] = sh0Codebook[sh0Idx2] ?? 0

      // 透明度 (alpha 通道，转为 logit 空间)
      const alpha = sh0Alpha / 255
      opacity[i] = alpha <= 0 ? -40 : alpha >= 1 ? 40 : -Math.log(1 / alpha - 1)
    }

    // 构建 SplatData
    const splatData = new SplatData()
    splatData.numSplats = count
    splatData.elements = [
      {
        name: 'vertex',
        count: count,
        properties: [
          { name: 'x', type: 'float', storage: x },
          { name: 'y', type: 'float', storage: y },
          { name: 'z', type: 'float', storage: z },
          { name: 'f_dc_0', type: 'float', storage: f_dc_0 },
          { name: 'f_dc_1', type: 'float', storage: f_dc_1 },
          { name: 'f_dc_2', type: 'float', storage: f_dc_2 },
          { name: 'opacity', type: 'float', storage: opacity },
          { name: 'scale_0', type: 'float', storage: scale_0 },
          { name: 'scale_1', type: 'float', storage: scale_1 },
          { name: 'scale_2', type: 'float', storage: scale_2 },
          { name: 'rot_0', type: 'float', storage: rot_0 },
          { name: 'rot_1', type: 'float', storage: rot_1 },
          { name: 'rot_2', type: 'float', storage: rot_2 },
          { name: 'rot_3', type: 'float', storage: rot_3 },
        ],
      },
    ]

    return splatData
  }

  /**
   * 从加载的数据中提取 SOGS 压缩格式
   */
  private extractChunkSogsData(
    meta: SOGChunkMeta,
    meansData: { low: Uint8Array; high: Uint8Array },
    scalesData: Uint8Array,
    quatsData: Uint8Array,
    sh0Data: Uint8Array,
    shNData: {
      shCentroids: Uint8Array
      shLabels: Uint8Array
      shCentroidsWidth?: number
      shCentroidsHeight?: number
    } | null,
    offset: number,
    count: number
  ): SogsData {
    // 提取指定范围的数据
    const pixelStart = offset * 4
    const pixelCount = count * 4

    const meansLow = meansData.low.slice(pixelStart, pixelStart + pixelCount)
    const meansHigh = meansData.high.slice(pixelStart, pixelStart + pixelCount)
    const quats = quatsData.slice(pixelStart, pixelStart + pixelCount)
    const scales = scalesData.slice(pixelStart, pixelStart + pixelCount)
    const sh0 = sh0Data.slice(pixelStart, pixelStart + pixelCount)

    // 提取 SH labels（与 splat 数据相同的偏移和范围）
    let shLabels: Uint8Array | undefined
    if (shNData?.shLabels) {
      shLabels = shNData.shLabels.slice(pixelStart, pixelStart + pixelCount)
    }

    // 构建元数据（正确处理 v1 和 v2 格式）
    const sogsMeta: SogsMeta = {
      version: meta.version as 1 | 2,
      means: {
        mins: meta.means.mins,
        maxs: meta.means.maxs,
      },
      scales: {
        ...(meta.version === 2 && meta.scales.codebook.length > 0
          ? { codebook: new Float32Array(meta.scales.codebook) }
          : {}),
        // v1 格式：从 meta 读取 mins/maxs（已在 upgradeMeta 中设置）
        ...(meta.scales.mins ? { mins: meta.scales.mins } : {}),
        ...(meta.scales.maxs ? { maxs: meta.scales.maxs } : {}),
      },
      sh0: {
        ...(meta.version === 2 && meta.sh0.codebook.length > 0
          ? { codebook: new Float32Array(meta.sh0.codebook) }
          : {}),
        // v1 格式：从 meta 读取 mins/maxs（已在 upgradeMeta 中设置）
        ...(meta.sh0.mins ? { mins: meta.sh0.mins } : {}),
        ...(meta.sh0.maxs ? { maxs: meta.sh0.maxs } : {}),
      },
      // 添加 shN 元数据
      ...(meta.shN
        ? {
            shN: {
              ...(meta.shN.count !== undefined ? { count: meta.shN.count } : {}),
              ...(meta.shN.bands !== undefined ? { bands: meta.shN.bands } : {}),
              ...(meta.version === 2 && meta.shN.codebook.length > 0
                ? { codebook: new Float32Array(meta.shN.codebook) }
                : {}),
              ...(meta.shN.mins !== undefined ? { mins: meta.shN.mins } : {}),
              ...(meta.shN.maxs !== undefined ? { maxs: meta.shN.maxs } : {}),
            },
          }
        : {}),
    }

    return new SogsData({
      meta: sogsMeta,
      numSplats: count,
      meansLow,
      meansHigh,
      quats,
      scales,
      sh0,
      // 传递 SH 数据
      ...(shLabels ? { shLabels } : {}),
      // shCentroids 是全局的 palette 数据，不需要按 splat 切片
      ...(shNData?.shCentroids ? { shCentroids: shNData.shCentroids } : {}),
      // 传递 shCentroids 的真实尺寸
      ...(shNData?.shCentroidsWidth ? { shCentroidsWidth: shNData.shCentroidsWidth } : {}),
      ...(shNData?.shCentroidsHeight ? { shCentroidsHeight: shNData.shCentroidsHeight } : {}),
    })
  }

  /**
   * 合并多个 SplatData
   */
  private mergeSplatData(dataList: (SplatData | SogsData)[]): SplatData {
    // 过滤出 SplatData
    const splatDataList = dataList.filter((d) => d instanceof SplatData) as SplatData[]

    if (splatDataList.length === 0) {
      const empty = new SplatData()
      empty.numSplats = 0
      empty.elements = []
      return empty
    }

    if (splatDataList.length === 1) {
      return splatDataList[0]
    }

    // 计算总数
    const totalCount = splatDataList.reduce((sum, d) => sum + d.numSplats, 0)

    // 获取属性名列表
    const propNames = splatDataList[0].elements[0].properties.map((p) => p.name)

    // 分配合并后的存储
    const mergedProps: Map<string, Float32Array> = new Map()
    for (const name of propNames) {
      mergedProps.set(name, new Float32Array(totalCount))
    }

    // 复制数据
    let offset = 0
    for (const data of splatDataList) {
      for (const name of propNames) {
        const src = data.getProp(name) as Float32Array
        const dst = mergedProps.get(name)!
        if (src) {
          dst.set(src, offset)
        }
      }
      offset += data.numSplats
    }

    // 构建合并后的 SplatData
    const merged = new SplatData()
    merged.numSplats = totalCount
    merged.elements = [
      {
        name: 'vertex',
        count: totalCount,
        properties: propNames.map((name) => ({
          name,
          type: 'float',
          storage: mergedProps.get(name)!,
        })),
      },
    ]

    return merged
  }

  /**
   * 合并多个 SogsData
   */
  private mergeSogsData(dataList: (SplatData | SogsData)[]): SogsData {
    // 过滤出 SogsData
    const sogsDataList = dataList.filter((d) => d instanceof SogsData) as SogsData[]

    if (sogsDataList.length === 0) {
      throw new Error('No SOGS data to merge')
    }

    if (sogsDataList.length === 1) {
      return sogsDataList[0]
    }

    // 计算总数
    const totalCount = sogsDataList.reduce((sum, d) => sum + d.numSplats, 0)

    // 分配合并后的存储
    const meansLow = new Uint8Array(totalCount * 4)
    const meansHigh = new Uint8Array(totalCount * 4)
    const quats = new Uint8Array(totalCount * 4)
    const scales = new Uint8Array(totalCount * 4)
    const sh0 = new Uint8Array(totalCount * 4)

    // 检查是否有 SH 数据
    const hasShData = sogsDataList.some((d) => d.shLabels && d.shCentroids)
    let shLabels: Uint8Array | undefined
    let shCentroids: Uint8Array | undefined

    if (hasShData) {
      shLabels = new Uint8Array(totalCount * 4)
      // shCentroids 是 palette 数据，使用第一个块的数据（假设所有块共享相同的 palette）
      shCentroids = sogsDataList.find((d) => d.shCentroids)?.shCentroids ?? undefined
    }

    // 复制数据
    let offset = 0
    for (const data of sogsDataList) {
      // 检查数据是否可用
      if (!data.meansLow || !data.meansHigh || !data.quats || !data.scales || !data.sh0) {
        throw new Error('[SOGLoader] 无法合并 SOGS 数据：数据已释放（minimalMemory 模式）')
      }

      const count = data.numSplats * 4
      meansLow.set(data.meansLow, offset)
      meansHigh.set(data.meansHigh, offset)
      quats.set(data.quats, offset)
      scales.set(data.scales, offset)
      sh0.set(data.sh0, offset)

      // 复制 SH labels
      if (shLabels && data.shLabels) {
        shLabels.set(data.shLabels, offset)
      }

      offset += count
    }

    // 使用第一个块的 meta（假设所有块使用相同的编码方式）
    return new SogsData({
      meta: sogsDataList[0].meta,
      numSplats: totalCount,
      meansLow,
      meansHigh,
      quats,
      scales,
      sh0,
      ...(shLabels ? { shLabels } : {}),
      ...(shCentroids ? { shCentroids } : {}),
    })
  }

  /**
   * 获取八叉树根节点包围盒。
   *
   * @returns asset-local 包围盒；尚未加载元数据时返回 null
   */
  getBoundingBox(): THREE.Box3 | null {
    if (!this.lodMeta?.tree?.bound) return null

    const b = this.lodMeta.tree.bound
    return new THREE.Box3(
      new THREE.Vector3(b.min[0], b.min[1], b.min[2]),
      new THREE.Vector3(b.max[0], b.max[1], b.max[2])
    )
  }

  /**
   * 获取 LOD 元数据，供外部 LOD 管理器使用。
   *
   * @returns 当前元数据；尚未加载时返回 null
   */
  getLodMeta(): SOGLodMeta | null {
    return this.lodMeta
  }

  /**
   * 按需加载指定块（用于LOD系统）
   * @param fileIndex 文件索引
   * @param offset 偏移量
   * @param count 数量
   * @returns 块数据（SplatData 或 SogsData）
   */
  async loadChunkOnDemand(
    fileIndex: number,
    offset: number,
    count: number
  ): Promise<SplatData | SogsData> {
    return this.loadChunk(fileIndex, offset, count)
  }

  /**
   * 加载指定 LOD 级别的所有块
   * @param lodLevel LOD 级别（0 = 最高质量）
   * @returns 合并后的数据
   */
  async loadLODLevel(lodLevel: number): Promise<SplatData | SogsData> {
    if (!this.lodMeta) {
      throw new Error('LOD 元数据未加载，请先调用 load()')
    }

    // 收集该 LOD 级别的所有块
    const chunks = this.collectChunksForLod(lodLevel)
    if (chunks.length === 0) {
      throw new Error(`LOD 级别 ${lodLevel} 没有数据`)
    }

    // 并行加载所有块
    const chunkDataList: (SplatData | SogsData)[] = []
    const batchSize = this.options.maxConcurrentLoads || 8

    for (let i = 0; i < chunks.length; i += batchSize) {
      const batch = chunks.slice(i, i + batchSize)
      const results = await Promise.all(
        batch.map((chunk) => this.loadChunk(chunk.fileIndex, chunk.offset, chunk.count))
      )
      chunkDataList.push(...results)
    }

    // 合并数据
    if (this.options.keepCompressed !== false) {
      return this.mergeSogsData(chunkDataList)
    } else {
      return this.mergeSplatData(chunkDataList)
    }
  }

  /**
   * 预加载 LOD 元数据（不加载块数据），用于快速初始化 LOD 系统。
   *
   * @param url - lod-meta.json 的 URL
   * @returns 解析完成的 LOD 元数据
   */
  async preloadMeta(url: string): Promise<SOGLodMeta> {
    // 检查中止信号
    if (this.options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    // 解析基础URL（先解码再提取，避免编码路径导致 baseUrl 提取错误）
    const decodedUrl = decodeURIComponent(url)
    this.baseUrl = decodedUrl.substring(0, decodedUrl.lastIndexOf('/') + 1)

    return log.perf.span('preloadMeta', async () => {
      const attempt = beginGSplatResourceAttempt(this.options.telemetry, {
        resourceType: 'lod-meta',
        resourceIdentity: url,
        resourceUrl: url,
      })
      try {
        const response = await fetch(url, this.fetchInit())
        attempt.markResponse(response)
        if (!response.ok) {
          attempt.fail(new Error('LOD metadata request failed'), `http-${response.status}`)
          throw new Error(`加载失败: ${response.status} ${response.statusText}`)
        }
        const buffer = await response.arrayBuffer()
        attempt.complete(buffer.byteLength)
        const lodMeta = measureGSplatLoadStageSync(
          this.options.telemetry,
          {
            resourceType: 'lod-meta',
            resourceIdentity: url,
            resourceUrl: url,
            stage: 'metadata-parse',
          },
          () => JSON.parse(new TextDecoder().decode(buffer)) as unknown
        )
        assertValidSOGLodMeta(lodMeta)
        this.lodMeta = lodMeta
        return lodMeta
      } catch (error) {
        attempt.fail(error)
        throw error
      }
    })
  }

  /**
   * 获取所有 LOD 级别信息。
   *
   * @returns 每个 LOD 的层级号与块数；尚未加载时返回 null
   */
  getLODInfo(): Array<{ level: number; chunkCount: number }> | null {
    if (!this.lodMeta) return null

    const info: Array<{ level: number; chunkCount: number }> = []
    for (let i = 0; i < this.lodMeta.lodLevels; i++) {
      const chunks = this.collectChunksForLod(i)
      info.push({ level: i, chunkCount: chunks.length })
    }
    return info
  }

  /**
   * 获取指定节点的 LOD 数据信息
   * @param node 八叉树节点
   * @returns LOD 数据信息数组
   */
  getNodeLODInfo(
    node: SOGNode
  ): Array<{ level: number; fileIndex: number; offset: number; count: number }> {
    const result: Array<{ level: number; fileIndex: number; offset: number; count: number }> = []

    if (node.lods) {
      for (const [levelStr, lodData] of Object.entries(node.lods)) {
        result.push({
          level: parseInt(levelStr, 10),
          fileIndex: lodData.file,
          offset: lodData.offset,
          count: lodData.count,
        })
      }
    }

    return result
  }

  /**
   * 清空块元数据缓存
   * 释放内存
   */
  clearChunkMetaCache(): void {
    this.chunkMetas.clear()
  }

  /**
   * 直接从子块 meta.json URL 加载数据
   * 用于 GSplatAssetLoader 按需加载单个子块
   *
   * @param metaUrl 子块 meta.json 的完整 URL（如 /output/0_0/meta.json）
   * @returns 加载的 SplatData 或 SogsData
   */
  async loadChunkFromMeta(metaUrl: string): Promise<SplatData | SogsData> {
    // 检查中止信号
    if (this.options.signal?.aborted) {
      throw new DOMException('Aborted', 'AbortError')
    }

    // 1. 下载子块的 meta.json
    const response = await fetch(metaUrl, this.fetchInit())
    if (!response.ok) {
      throw new Error(`加载子块 meta 失败: ${response.status} ${response.statusText}`)
    }
    const rawMeta = await response.json()

    // 2. 处理 v1 格式（升级到 v2 格式）
    const chunkMeta = this.upgradeMeta(rawMeta)

    // 3. 计算子块目录（去掉 meta.json）
    const chunkDir = metaUrl.substring(0, metaUrl.lastIndexOf('/') + 1)

    // 4. 并行加载所有 WebP 文件
    const [meansData, scalesData, quatsData, sh0Data] = await Promise.all([
      this.loadMeansWebPFromDir(chunkDir, chunkMeta),
      this.loadScalesWebPFromDir(chunkDir, chunkMeta),
      this.loadQuatsWebPFromDir(chunkDir, chunkMeta),
      this.loadSH0WebPFromDir(chunkDir, chunkMeta),
    ])

    // 5. 提取全部数据（offset=0, count=全部）
    const offset = 0
    const count = chunkMeta.count

    if (this.options.keepCompressed !== false) {
      return this.extractChunkSogsData(
        chunkMeta,
        meansData,
        scalesData,
        quatsData,
        sh0Data,
        null, // loadChunkFromMeta 不加载 SH 高阶数据
        offset,
        count
      )
    } else {
      return this.extractChunkData(
        chunkMeta,
        meansData,
        scalesData,
        quatsData,
        sh0Data,
        offset,
        count
      )
    }
  }

  /**
   * 将 v1 格式的 meta 升级为 v2 格式
   * 参考 PlayCanvas sogs.js 的 upgradeMeta 函数
   */
  private upgradeMeta(meta: SOGChunkMeta | Record<string, unknown>): SOGChunkMeta {
    // 检查是否已经是v2格式
    if ('version' in meta && typeof meta.version === 'number' && meta.version === 2) {
      return meta as SOGChunkMeta
    }

    // 将 meta 视为 v1 格式
    const v1Meta = meta as unknown as SOGChunkMetaV1

    // 升级v1到v2
    const result: SOGChunkMeta = {
      version: 1,
      count: v1Meta.means?.shape?.[0] ?? 0,
      means: {
        mins: v1Meta.means?.mins ?? [0, 0, 0],
        maxs: v1Meta.means?.maxs ?? [0, 0, 0],
        files: v1Meta.means?.files ?? [],
      },
      scales: {
        ...(v1Meta.scales?.mins ? { mins: v1Meta.scales.mins } : {}),
        ...(v1Meta.scales?.maxs ? { maxs: v1Meta.scales.maxs } : {}),
        codebook: [], // v1 没有 codebook
        files: v1Meta.scales?.files ?? [],
      },
      quats: {
        files: v1Meta.quats?.files ?? [],
      },
      sh0: {
        ...(v1Meta.sh0?.mins ? { mins: v1Meta.sh0.mins } : {}),
        ...(v1Meta.sh0?.maxs ? { maxs: v1Meta.sh0.maxs } : {}),
        codebook: [], // v1 没有 codebook
        files: v1Meta.sh0?.files ?? [],
      },
    }

    // 处理 shN（如果存在）
    if (v1Meta.shN) {
      result.shN = {
        ...(v1Meta.shN.mins !== undefined ? { mins: v1Meta.shN.mins } : {}),
        ...(v1Meta.shN.maxs !== undefined ? { maxs: v1Meta.shN.maxs } : {}),
        codebook: [],
        files: v1Meta.shN.files ?? [],
      }
    }

    return result
  }

  /**
   * 从指定目录加载位置 WebP 数据
   */
  private async loadMeansWebPFromDir(
    chunkDir: string,
    meta: SOGChunkMeta
  ): Promise<{ low: Uint8Array; high: Uint8Array }> {
    const [lowResult, highResult] = await Promise.all([
      this.loadWebPAsImageData(chunkDir + meta.means.files[0]),
      this.loadWebPAsImageData(chunkDir + meta.means.files[1]),
    ])
    return { low: lowResult.data, high: highResult.data }
  }

  /**
   * 从指定目录加载缩放 WebP 数据
   */
  private async loadScalesWebPFromDir(chunkDir: string, meta: SOGChunkMeta): Promise<Uint8Array> {
    const result = await this.loadWebPAsImageData(chunkDir + meta.scales.files[0])
    return result.data
  }

  /**
   * 从指定目录加载四元数 WebP 数据
   */
  private async loadQuatsWebPFromDir(chunkDir: string, meta: SOGChunkMeta): Promise<Uint8Array> {
    const result = await this.loadWebPAsImageData(chunkDir + meta.quats.files[0])
    return result.data
  }

  /**
   * 从指定目录加载 SH0 WebP 数据
   */
  private async loadSH0WebPFromDir(chunkDir: string, meta: SOGChunkMeta): Promise<Uint8Array> {
    const result = await this.loadWebPAsImageData(chunkDir + meta.sh0.files[0])
    return result.data
  }

  /**
   * 从内存中的文件数据加载单个子块
   * 用于 SOGBundleLoader
   *
   * @param meta 子块元数据（支持 v1 和 v2 格式）
   * @param fileMap 文件名到数据的映射
   * @returns 加载的 SplatData 或 SogsData
   */
  async loadChunkFromMemory(
    meta: SOGChunkMeta | Record<string, unknown>,
    fileMap: Map<string, Uint8Array>
  ): Promise<SplatData | SogsData> {
    // 升级 meta 到 v2 格式（如果需要）
    const chunkMeta = this.upgradeMeta(meta)

    // 加载 WebP 图片（从内存数据）
    const [meansData, scalesData, quatsData, sh0Data, shNData] = await Promise.all([
      this.loadMeansWebPFromMemory(chunkMeta, fileMap),
      this.loadScalesWebPFromMemory(chunkMeta, fileMap),
      this.loadQuatsWebPFromMemory(chunkMeta, fileMap),
      this.loadSH0WebPFromMemory(chunkMeta, fileMap),
      this.loadShNWebPFromMemory(chunkMeta, fileMap),
    ])

    // 提取全部数据
    const offset = 0
    const count = chunkMeta.count

    if (this.options.keepCompressed !== false) {
      return this.extractChunkSogsData(
        chunkMeta,
        meansData,
        scalesData,
        quatsData,
        sh0Data,
        shNData,
        offset,
        count
      )
    } else {
      return this.extractChunkData(
        chunkMeta,
        meansData,
        scalesData,
        quatsData,
        sh0Data,
        offset,
        count
      )
    }
  }

  /**
   * 从内存数据加载位置 WebP
   */
  private async loadMeansWebPFromMemory(
    meta: SOGChunkMeta,
    fileMap: Map<string, Uint8Array>
  ): Promise<{ low: Uint8Array; high: Uint8Array }> {
    const lowData = fileMap.get(meta.means.files[0])
    const highData = fileMap.get(meta.means.files[1])

    if (!lowData || !highData) {
      throw new Error(`缺少位置数据文件: ${meta.means.files[0]} 或 ${meta.means.files[1]}`)
    }

    const [lowResult, highResult] = await Promise.all([
      this.decodeWebPFromMemory(lowData),
      this.decodeWebPFromMemory(highData),
    ])

    return { low: lowResult.data, high: highResult.data }
  }

  /**
   * 从内存数据加载缩放 WebP
   */
  private async loadScalesWebPFromMemory(
    meta: SOGChunkMeta,
    fileMap: Map<string, Uint8Array>
  ): Promise<Uint8Array> {
    const data = fileMap.get(meta.scales.files[0])
    if (!data) {
      throw new Error(`缺少缩放数据文件: ${meta.scales.files[0]}`)
    }
    const result = await this.decodeWebPFromMemory(data)
    return result.data
  }

  /**
   * 从内存数据加载四元数 WebP
   */
  private async loadQuatsWebPFromMemory(
    meta: SOGChunkMeta,
    fileMap: Map<string, Uint8Array>
  ): Promise<Uint8Array> {
    const data = fileMap.get(meta.quats.files[0])
    if (!data) {
      throw new Error(`缺少四元数数据文件: ${meta.quats.files[0]}`)
    }
    const result = await this.decodeWebPFromMemory(data)
    return result.data
  }

  /**
   * 从内存数据加载 SH0 WebP
   */
  private async loadSH0WebPFromMemory(
    meta: SOGChunkMeta,
    fileMap: Map<string, Uint8Array>
  ): Promise<Uint8Array> {
    const data = fileMap.get(meta.sh0.files[0])
    if (!data) {
      throw new Error(`缺少 SH0 数据文件: ${meta.sh0.files[0]}`)
    }
    const result = await this.decodeWebPFromMemory(data)
    return result.data
  }

  /**
   * 从内存数据加载 SHN WebP（球谐高阶系数）
   * 返回数据和尺寸信息
   */
  private async loadShNWebPFromMemory(
    meta: SOGChunkMeta,
    fileMap: Map<string, Uint8Array>
  ): Promise<{
    shCentroids: Uint8Array
    shLabels: Uint8Array
    shCentroidsWidth: number
    shCentroidsHeight: number
  } | null> {
    if (!meta.shN?.files || meta.shN.files.length < 2) {
      return null
    }

    const centroidsData = fileMap.get(meta.shN.files[0])
    const labelsData = fileMap.get(meta.shN.files[1])

    if (!centroidsData || !labelsData) {
      return null
    }

    const [centroidsResult, labelsResult] = await Promise.all([
      this.decodeWebPFromMemory(centroidsData),
      this.decodeWebPFromMemory(labelsData),
    ])

    return {
      shCentroids: centroidsResult.data,
      shLabels: labelsResult.data,
      shCentroidsWidth: centroidsResult.width,
      shCentroidsHeight: centroidsResult.height,
    }
  }

  /**
   * WebP 解码结果（包含尺寸信息）
   */
  private async decodeWebPFromMemory(
    webpData: Uint8Array
  ): Promise<{ data: Uint8Array; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      // 创建 Blob URL
      const buffer = webpData.buffer.slice(
        webpData.byteOffset,
        webpData.byteOffset + webpData.byteLength
      ) as ArrayBuffer
      const blob = new Blob([buffer], { type: 'image/webp' })
      const url = URL.createObjectURL(blob)

      const img = new Image()
      img.crossOrigin = 'anonymous'

      img.onload = () => {
        // 释放 Blob URL
        URL.revokeObjectURL(url)

        const canvas = document.createElement('canvas')
        canvas.width = img.width
        canvas.height = img.height

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          reject(new Error('无法创建 canvas context'))
          return
        }

        ctx.drawImage(img, 0, 0)
        const imageData = ctx.getImageData(0, 0, img.width, img.height)
        resolve({
          data: new Uint8Array(imageData.data.buffer),
          width: img.width,
          height: img.height,
        })
      }

      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('解码 WebP 失败'))
      }

      img.src = url
    })
  }
}
